import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { queryClient } from "@/lib/react-query";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { SecurityDashboard } from "@/pages/SecurityDashboard";
import { IssuesPage } from "@/pages/IssuesPage";
import { IssueDetailPage } from "@/pages/IssueDetailPage";
import { AlertsListPage } from "@/pages/AlertsListPage";
import { CreateAlertPage } from "@/pages/CreateAlertPage";
import { EditAlertPage } from "@/pages/EditAlertPage";
import { LoginPage } from "@/pages/LoginPage";
import { DemoFlowPage } from "@/pages/DemoFlowPage";
import { LLMScansListPage } from "@/pages/LLMScansListPage";
import { LLMScanResultsPage } from "@/pages/LLMScanResultsPage";
import { SharedReportPage } from "@/pages/SharedReportPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Printer, Mail } from "lucide-react";
import { Auth0ProviderWithCallback } from "@/providers/Auth0Provider";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { FeatureGate } from "@/components/auth/FeatureGate";
import { PostHogPageviewTracker } from "@/components/analytics/PostHogPageviewTracker";
import { CrispChat } from "@/components/analytics/CrispChat";
import { AppTourStylesProvider } from "@/providers/AppTourStylesProvider";
import { AppTourLauncher } from "@/components/onboarding/AppTourLauncher";
import { apiClient } from "@/services/api";
import { useScanQuota } from "@/hooks/useScanQuota";
import type { UserData } from "@/types/user";
import "./App.css";

/**
 * Render the application's dashboard welcome panel.
 *
 * @returns The dashboard UI as a React element.
 */
function Dashboard() {
  return (
    <div className="flex flex-1 flex-col gap-4 p-6">
      <div className="rounded-lg border bg-card p-6">
        <h2 className="text-2xl font-semibold mb-4">Welcome to EarlyCore</h2>
        <p className="text-muted-foreground">
          Your platform is ready. Use the sidebar to navigate between Dashboard, Security Issues,
          Alerts & Policies, and Settings.
        </p>
      </div>
    </div>
  );
}

function AppHeader() {
  const location = useLocation();
  const isSecurityDashboard = location.pathname === "/security";

  // Fetch current user to get username
  const { data: userData } = useQuery<UserData>({
    queryKey: ["user", "me"],
    queryFn: async () => {
      const data = await apiClient.get<UserData>("/api/v1/user/me/");
      return data;
    },
    staleTime: 30000, // 30 seconds
  });

  // Fetch scan quota
  const { data: quota, isLoading: quotaLoading } = useScanQuota(userData?.username);

  return (
    <header className="flex h-12 items-center justify-between border-b bg-background px-6">
      <div className="flex items-center gap-4">
        <SidebarTrigger />
      </div>
      <div className="flex items-center gap-4">
        {/* Scan Quota Display */}
        {!quotaLoading && quota && !quota.is_unlimited && (
          <div className="text-sm" data-tour="quota-counter">
            <span className="text-muted-foreground">Reports used: </span>
            <span className="font-medium">
              {quota.used_reports}/{quota.max_reports ?? "unlimited"}
            </span>
            {quota.remaining_reports === 0 && (
              <a
                href="mailto:support@earlycore.dev?subject=Increase Report Limit"
                className="ml-2 text-xs text-primary hover:underline"
              >
                Upgrade
              </a>
            )}
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="flex items-center gap-2 px-3 py-1"
            data-tour="feedback-button"
          >
            <a
              href="mailto:paul@earlycore.dev?subject=EarlyCore Feedback"
              title="Send us feedback – we want your input!"
              style={{ display: "flex", alignItems: "center", textDecoration: "none" }}
            >
              <Mail className="h-5 w-5 mr-1" />
              <span className="hidden sm:inline font-medium">Send Feedback</span>
              <span className="sr-only">Send feedback (opens mail client)</span>
            </a>
          </Button>
          {isSecurityDashboard && (
            <Button variant="ghost" size="icon" onClick={() => window.print()}>
              <Printer className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}

/**
 * Root application component that configures global providers, routing, and the main app layout.
 *
 * Renders the QueryClient, router, and authentication providers, exposes public routes (e.g., login and shared reports),
 * and the authenticated application layout containing the sidebar, header, and gated protected routes.
 *
 * @returns The top-level React element for the application, including providers and route definitions.
 */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <PostHogPageviewTracker />
        <Auth0ProviderWithCallback>
          <AppTourStylesProvider>
            <CrispChat />
            <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/share/:token" element={<SharedReportPage />} />

            {/* Protected routes - require authentication */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <AppSidebar />
                    <main className="w-full">
                      <AppHeader />
                      <AppTourLauncher />
                      <Routes>
                        <Route path="/" element={<Navigate to="/compliance" replace />} />
                        <Route
                          path="/security"
                          element={
                            <FeatureGate feature="security_dashboard">
                              <SecurityDashboard />
                            </FeatureGate>
                          }
                        />
                        <Route
                          path="/issues"
                          element={
                            <FeatureGate feature="issues">
                              <IssuesPage />
                            </FeatureGate>
                          }
                        />
                        <Route
                          path="/issues/:issueId"
                          element={
                            <FeatureGate feature="issues">
                              <IssueDetailPage />
                            </FeatureGate>
                          }
                        />
                        <Route
                          path="/alerts"
                          element={
                            <FeatureGate feature="alerts">
                              <AlertsListPage />
                            </FeatureGate>
                          }
                        />
                        <Route
                          path="/alerts/new"
                          element={
                            <FeatureGate feature="alerts">
                              <CreateAlertPage />
                            </FeatureGate>
                          }
                        />
                        <Route
                          path="/alerts/:id/edit"
                          element={
                            <FeatureGate feature="alerts">
                              <EditAlertPage />
                            </FeatureGate>
                          }
                        />
                        <Route
                          path="/compliance"
                          element={
                            <FeatureGate feature="demo_flow">
                              <DemoFlowPage />
                            </FeatureGate>
                          }
                        />
                        <Route
                          path="/llm-scans"
                          element={
                            <FeatureGate feature="llm_scans">
                              <LLMScansListPage />
                            </FeatureGate>
                          }
                        />
                        <Route
                          path="/llm-scans/:scanUuid/results"
                          element={
                            <FeatureGate feature="llm_scans">
                              <LLMScanResultsPage />
                            </FeatureGate>
                          }
                        />
                        <Route path="/settings" element={<SettingsPage />} />
                        <Route path="/billing" element={<Navigate to="/settings?section=billing" replace />} />
                        <Route path="/profile" element={<Navigate to="/settings?section=profile" replace />} />
                      </Routes>
                    </main>
                  </SidebarProvider>
                </ProtectedRoute>
              }
            />
          </Routes>
          <ReactQueryDevtools initialIsOpen={false} />
          </AppTourStylesProvider>
        </Auth0ProviderWithCallback>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
